import { useState, useCallback, useEffect, useRef } from "react";
import { API_ENDPOINTS } from "@/lib/api";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BannerSlide {
  zhTitle: string;
  zhSub: string;
  enTitle: string;
  enSub: string;
  image: string;
  videoUrl?: string;
}

export interface AboutStat {
  num: string;
  zhLabel: string;
  enLabel: string;
}

export interface ProductItem {
  zhTitle: string;
  zhDesc: string;
  enTitle: string;
  enDesc: string;
  image: string;
}

export interface ContactInfoItem {
  icon: string;
  zhLabel: string;
  enLabel: string;
  zhValue: string;
  enValue: string;
}

export interface FriendLink {
  label: string;
  href: string;
}

export interface SocialLink {
  id: string;
  icon: string;
  label: string;
  href: string;
  visible: boolean;
}

export type BgMusicPlaybackStrategy = "audible-first" | "muted-fallback" | "manual";

export interface BgMusic {
  name: string;
  enabled: boolean;
  url: string;
  volume: number;
  strategy: BgMusicPlaybackStrategy;
}

export interface FooterLegal {
  copyright: string;
  privacyZh: string;
  privacyEn: string;
  privacyHref: string;
  termsZh: string;
  termsEn: string;
  termsHref: string;
  icpZh: string;
  icpEn: string;
  icpHref: string;
}

export interface ContactContent {
  zhTitle: string;
  enTitle: string;
  zhSubtitle: string;
  enSubtitle: string;
  zhFormTitle: string;
  enFormTitle: string;
  infoItems: ContactInfoItem[];
  mapEmbedUrl: string;
}

export interface AIChatConfig {
  enabled: boolean;
  endpoint: string;
}

export interface NavItem {
  zh: string;
  en: string;
  href: string;
  scroll: boolean;
}

export interface NavContent {
  logoImage: string;
  logoAlt: string;
  ogTitle: string;
  items: NavItem[];
}

export interface ProductSpec {
  label: string;
  value: string;
}

export interface ProductLocale {
  title: string;
  subtitle: string;
  tags: string[];
  specs: ProductSpec[];
  desc: string;
  features: string[];
}

export interface ProductDetail {
  id: string;
  category: string;
  accent: string;
  images: string[];
  zh: ProductLocale;
  en: ProductLocale;
}

export interface TechServiceDoc {
  id: string;
  order: number;
  zhTitle: string;
  enTitle: string;
  zhSummary: string;
  enSummary: string;
  zhContentHtml: string;
  enContentHtml: string;
}

export interface NewsArticle {
  id: string;
  order: number;
  publishDate: string;
  zhTitle: string;
  enTitle: string;
  zhSummary: string;
  enSummary: string;
  zhContentHtml: string;
  enContentHtml: string;
}

export interface ProductCategory {
  id: string;
  zh: string;
  en: string;
}

export interface SiteContent {
  bannerSlides: BannerSlide[];
  aboutStats: AboutStat[];
  aboutZhDesc1: string;
  aboutZhDesc2: string;
  aboutEnDesc1: string;
  aboutEnDesc2: string;
  aboutImage: string;
  products: ProductItem[];
  contact: ContactContent;
  nav: NavContent;
  productsDetail: ProductDetail[];
  productCategories: ProductCategory[];
  friendLinks: FriendLink[];
  socialLinks: SocialLink[];
  footerLegal: FooterLegal;
  bgMusic: BgMusic;
  aiChat: AIChatConfig;
  techServiceDocs: TechServiceDoc[];
  newsArticles: NewsArticle[];
}

const BC_CHANNEL = "xinghe_content_sync";

// ─── 全局单例 store（确保所有组件共享同一个 state） ────────────────────────────
type Listener = () => void;
let _content: SiteContent | null = null;
let _loading = true;
const _listeners = new Set<Listener>();

function getCurrentContent(): SiteContent {
  if (!_content) _content = defaultContent;
  return _content;
}

function normalizeMusicVolume(volume: unknown): number {
  if (typeof volume !== "number" || Number.isNaN(volume)) return defaultBgMusic.volume;
  return Math.min(1, Math.max(0, volume));
}

function normalizeMusicStrategy(strategy: unknown): BgMusicPlaybackStrategy {
  if (strategy === "muted-fallback" || strategy === "manual") return strategy;
  return "audible-first";
}

function mergeContent(saved: Partial<SiteContent> | null | undefined): SiteContent {
  if (!saved || typeof saved !== "object") return defaultContent;
  return {
    ...defaultContent, ...saved,
    nav: saved.nav ? { ...defaultContent.nav, ...saved.nav, items: saved.nav.items ?? defaultContent.nav.items } : defaultContent.nav,
    bannerSlides: saved.bannerSlides ?? defaultContent.bannerSlides,
    products: saved.products ?? defaultContent.products,
    aboutStats: saved.aboutStats ?? defaultContent.aboutStats,
    contact: saved.contact ? { ...defaultContent.contact, ...saved.contact, infoItems: saved.contact.infoItems ?? defaultContent.contact.infoItems } : defaultContent.contact,
    productsDetail: saved.productsDetail ?? defaultContent.productsDetail,
    productCategories: saved.productCategories ?? defaultContent.productCategories,
    friendLinks: saved.friendLinks ?? defaultContent.friendLinks,
    socialLinks: saved.socialLinks ?? defaultContent.socialLinks,
    footerLegal: saved.footerLegal ? { ...defaultContent.footerLegal, ...saved.footerLegal } : defaultContent.footerLegal,
    techServiceDocs: saved.techServiceDocs ?? defaultContent.techServiceDocs,
    newsArticles: saved.newsArticles ?? defaultContent.newsArticles,
    aiChat: saved.aiChat
      ? {
          ...defaultContent.aiChat,
          ...saved.aiChat,
          enabled: typeof saved.aiChat.enabled === "boolean" ? saved.aiChat.enabled : defaultContent.aiChat.enabled,
          endpoint: saved.aiChat.endpoint?.trim() ?? defaultContent.aiChat.endpoint,
        }
      : defaultContent.aiChat,
    bgMusic: saved.bgMusic
      ? {
          ...defaultContent.bgMusic,
          ...saved.bgMusic,
          url: saved.bgMusic.url?.trim() || defaultContent.bgMusic.url,
          volume: normalizeMusicVolume(saved.bgMusic.volume),
          strategy: normalizeMusicStrategy(saved.bgMusic.strategy),
        }
      : defaultContent.bgMusic,
  };
}

function notifyListeners() { _listeners.forEach(fn => fn()); }

function setGlobalContent(c: SiteContent) {
  _content = mergeContent(c);
  notifyListeners();
}

function setGlobalLoading(v: boolean) { _loading = v; notifyListeners(); }

// 启动时从部署 API 拉一次
let _initStarted = false;
function initGlobalStore() {
  if (_initStarted) return;
  _initStarted = true;
  loadFromServer().then((remote) => {
    if (remote) setGlobalContent(remote);
  }).catch(() => {}).finally(() => setGlobalLoading(false));
}

function broadcastContentUpdate() {
  try {
    const bc = new BroadcastChannel(BC_CHANNEL);
    bc.postMessage({ type: "content-updated", ts: Date.now() });
    bc.close();
  } catch {
    // BroadcastChannel not supported in this environment
  }
}

/** 从同域部署 API 加载内容，失败时返回 null */
async function loadFromServer(): Promise<SiteContent | null> {
  try {
    const res = await fetch(API_ENDPOINTS.content, { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const data = (await res.json()) as { content?: Partial<SiteContent> };
    if (!data.content) return null;
    const saved = data.content;
    return {
      ...defaultContent,
      ...saved,
      nav: saved.nav
        ? { ...defaultContent.nav, ...saved.nav, items: saved.nav.items ?? defaultContent.nav.items }
        : defaultContent.nav,
      bannerSlides: saved.bannerSlides ?? defaultContent.bannerSlides,
      products: saved.products ?? defaultContent.products,
      aboutStats: saved.aboutStats ?? defaultContent.aboutStats,
      contact: saved.contact
        ? { ...defaultContent.contact, ...saved.contact, infoItems: saved.contact.infoItems ?? defaultContent.contact.infoItems }
        : defaultContent.contact,
      productsDetail: saved.productsDetail ?? defaultContent.productsDetail,
      productCategories: saved.productCategories ?? defaultContent.productCategories,
      friendLinks: saved.friendLinks ?? defaultContent.friendLinks,
      socialLinks: saved.socialLinks ?? defaultContent.socialLinks,
      footerLegal: saved.footerLegal ? { ...defaultContent.footerLegal, ...saved.footerLegal } : defaultContent.footerLegal,
      techServiceDocs: saved.techServiceDocs ?? defaultContent.techServiceDocs,
      aiChat: saved.aiChat
        ? {
            ...defaultContent.aiChat,
            ...saved.aiChat,
            enabled: typeof saved.aiChat.enabled === "boolean" ? saved.aiChat.enabled : defaultContent.aiChat.enabled,
            endpoint: saved.aiChat.endpoint?.trim() ?? defaultContent.aiChat.endpoint,
          }
        : defaultContent.aiChat,
      bgMusic: saved.bgMusic
        ? {
            ...defaultContent.bgMusic,
            ...saved.bgMusic,
            url: saved.bgMusic.url?.trim() || defaultContent.bgMusic.url,
            volume: normalizeMusicVolume(saved.bgMusic.volume),
            strategy: normalizeMusicStrategy(saved.bgMusic.strategy),
          }
        : defaultContent.bgMusic,
    };
  } catch {
    return null;
  }
}

/** 保存内容到同域部署 API */
async function saveToServer(content: SiteContent): Promise<void> {
  const res = await fetch(API_ENDPOINTS.content, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ content }),
  });

  if (!res.ok) {
    const errText = await res.text();
    let detail = errText;
    try {
      const parsed = JSON.parse(errText) as { error?: string };
      detail = parsed.error || errText;
    } catch {
      // Keep plain text body when response is not JSON.
    }
    console.error("[saveToServer] API failed:", res.status, detail);
    throw new Error(detail || `Save failed: ${res.status}`);
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("Save API returned non-JSON response");
  }

  const result = (await res.json()) as { success?: boolean; error?: string };
  if (!result.success) throw new Error(result.error || "Save API did not confirm success");
}

export interface SaveResult {
  ok: boolean;
  error?: string;
}

// ─── Default product detail data ────────────────────────────────────────────

export const defaultProductCategories: ProductCategory[] = [
  { id: "aero-engine", zh: "航空发动机", en: "Aero Engine" },
  { id: "uav-propulsion", zh: "无人机动力", en: "UAV Propulsion" },
  { id: "precision-mfg", zh: "精密制造", en: "Precision Mfg" },
  { id: "tech-services", zh: "技术服务", en: "Tech Services" },
];

export const defaultProductsDetail: ProductDetail[] = [
  {
    id: "ae-001",
    category: "aero-engine",
    accent: "cyan",
    images: [
      "/local-images/34e862dea5bc.jpg",
      "/local-images/5b043bb56475.jpg",
      "/local-images/8ea6eb89ca16.jpg",
    ],
    zh: {
      title: "XH-TF200 涡扇发动机",
      subtitle: "高旁路比大推力民航级涡扇发动机",
      tags: ["涡扇发动机", "大推力", "民航级", "低油耗"],
      specs: [
        { label: "最大推力", value: "200 kN" },
        { label: "旁路比", value: "8.5:1" },
        { label: "耗油率", value: "0.55 kg/N·h" },
        { label: "整机重量", value: "2,850 kg" },
        { label: "适用飞机", value: "干线客机" },
        { label: "认证状态", value: "CCAR-33 适航认证" },
      ],
      desc: "XH-TF200 是星核动力科技自主研发的新一代高旁路比涡扇发动机，采用先进的三维叶片气动设计与新型热端材料，在保持高推力输出的同时大幅降低燃油消耗率与排放水平。核心压气机采用全三维优化叶片，整体效率提升12%；燃烧室运用先进贫油燃烧技术，NOx排放较同类发动机降低35%。发动机配备智能全权限数字电子控制系统（FADEC），确保在全飞行包线内的高可靠性与安全性。",
      features: [
        "先进三维叶片气动设计，压气机效率业界领先",
        "贫油预混燃烧技术，NOx排放降低35%",
        "全权限数字电子控制（FADEC）系统",
        "新型热障涂层，涡轮入口温度提高80K",
        "模块化设计，在翼维护时间缩短40%",
      ],
    },
    en: {
      title: "XH-TF200 Turbofan Engine",
      subtitle: "High Bypass Ratio Turbofan for Commercial Aviation",
      tags: ["Turbofan", "High Thrust", "Commercial", "Low Fuel Burn"],
      specs: [
        { label: "Max Thrust", value: "200 kN" },
        { label: "Bypass Ratio", value: "8.5:1" },
        { label: "TSFC", value: "0.55 kg/N·h" },
        { label: "Engine Weight", value: "2,850 kg" },
        { label: "Application", value: "Mainline Aircraft" },
        { label: "Certification", value: "CCAR-33 Certified" },
      ],
      desc: "The XH-TF200 is a next-generation high bypass ratio turbofan engine independently developed by Xinghe Propulsion Technology. It features advanced 3D aerodynamic blade design and novel hot-section materials to deliver high thrust while significantly reducing fuel consumption and emissions.",
      features: [
        "Advanced 3D blade aerodynamic design with industry-leading compressor efficiency",
        "Lean premix combustion technology, reducing NOx emissions by 35%",
        "Full Authority Digital Engine Control (FADEC) system",
        "New thermal barrier coatings, turbine inlet temperature increased by 80K",
        "Modular design reduces on-wing maintenance time by 40%",
      ],
    },
  },
  {
    id: "ae-002",
    category: "aero-engine",
    accent: "cyan",
    images: [
      "/local-images/c57ecf2ec47c.jpg",
      "/local-images/33e9f5de11fc.jpg",
      "/local-images/9da49945da4c.jpg",
    ],
    zh: {
      title: "XH-TS800 涡轴发动机",
      subtitle: "高功率密度直升机专用涡轴发动机",
      tags: ["涡轴发动机", "直升机", "高功率密度", "双余度控制"],
      specs: [
        { label: "起飞功率", value: "800 kW" },
        { label: "功率重量比", value: "5.8 kW/kg" },
        { label: "整机重量", value: "138 kg" },
        { label: "耗油率", value: "285 g/kW·h" },
        { label: "适用机型", value: "中型直升机" },
        { label: "认证状态", value: "CCAR-33 适航认证" },
      ],
      desc: "XH-TS800 涡轴发动机专为中型直升机动力需求设计，采用离心-轴流组合压气机与一体化减速齿轮箱，实现了极高的功率重量比。双余度 FADEC 控制系统保障飞行安全，自由涡轮设计使发动机能在宽泛的旋翼转速范围内输出稳定扭矩。",
      features: [
        "离心-轴流组合压气机，结构紧凑且效率高",
        "一体化减速齿轮箱，传动效率 ≥98.5%",
        "双余度 FADEC 控制，极高飞行安全冗余",
        "自由涡轮设计，宽转速范围稳定输出扭矩",
        "TBO 3,500 小时，全寿命 10,000 小时",
      ],
    },
    en: {
      title: "XH-TS800 Turboshaft Engine",
      subtitle: "High Power Density Turboshaft for Helicopters",
      tags: ["Turboshaft", "Helicopter", "High Power Density", "Dual Redundancy"],
      specs: [
        { label: "Takeoff Power", value: "800 kW" },
        { label: "Power-to-Weight", value: "5.8 kW/kg" },
        { label: "Engine Weight", value: "138 kg" },
        { label: "SFC", value: "285 g/kW·h" },
        { label: "Application", value: "Medium Helicopters" },
        { label: "Certification", value: "CCAR-33 Certified" },
      ],
      desc: "The XH-TS800 turboshaft engine is designed for medium helicopter power requirements, featuring a centrifugal-axial combined compressor and integrated reduction gearbox for exceptional power-to-weight ratio.",
      features: [
        "Centrifugal-axial combined compressor for compact, high-efficiency design",
        "Integrated reduction gearbox with ≥98.5% transmission efficiency",
        "Dual redundancy FADEC control for maximum flight safety",
        "Free turbine design for stable torque output across wide rotor speed range",
        "TBO 3,500 hours, design life 10,000 flight hours",
      ],
    },
  },
  {
    id: "ae-003",
    category: "aero-engine",
    accent: "cyan",
    images: [
      "/local-images/b29687dca0f7.jpg",
      "/local-images/537f3da2498e.jpg",
    ],
    zh: {
      title: "XH-P360 活塞发动机",
      subtitle: "通航飞机用高可靠航空活塞发动机",
      tags: ["活塞发动机", "通航飞机", "高可靠", "燃油直喷"],
      specs: [
        { label: "最大功率", value: "268 kW (360 hp)" },
        { label: "汽缸数", value: "6缸对置" },
        { label: "整机重量", value: "185 kg" },
        { label: "排量", value: "5,916 cm³" },
        { label: "耗油率", value: "258 g/kW·h" },
        { label: "认证状态", value: "CCAR-33 适航认证" },
      ],
      desc: "XH-P360 是专为通用航空飞机设计的六缸对置式航空活塞发动机。采用电子燃油直喷技术（EFI）与全权限数字发动机控制，显著提升燃油效率并降低排放。发动机拥有优秀的高空性能，配合增压器可在高原机场保持充足功率输出。",
      features: [
        "电子燃油直喷（EFI），燃效提升15%",
        "数字点火系统，优化各工况燃烧时序",
        "航空级气缸，服役寿命2,000小时",
        "配套自动变距螺旋桨控制接口",
        "支持含铅及无铅航空汽油",
      ],
    },
    en: {
      title: "XH-P360 Piston Engine",
      subtitle: "High-Reliability Aircraft Piston Engine for GA",
      tags: ["Piston Engine", "General Aviation", "High Reliability", "EFI"],
      specs: [
        { label: "Max Power", value: "268 kW (360 hp)" },
        { label: "Cylinders", value: "6-Cylinder Opposed" },
        { label: "Engine Weight", value: "185 kg" },
        { label: "Displacement", value: "5,916 cm³" },
        { label: "SFC", value: "258 g/kW·h" },
        { label: "Certification", value: "CCAR-33 Certified" },
      ],
      desc: "The XH-P360 is a six-cylinder opposed aircraft piston engine designed for general aviation. Electronic Fuel Injection (EFI) and full-authority digital engine control significantly improve fuel efficiency and reduce emissions.",
      features: [
        "Electronic Fuel Injection (EFI) improves fuel efficiency by 15%",
        "Digital ignition system optimizes combustion timing across all conditions",
        "Aviation-grade cylinders with 2,000-hour service life",
        "Compatible with constant-speed propeller control interface",
        "Supports both leaded and unleaded aviation gasoline",
      ],
    },
  },
  {
    id: "uav-001",
    category: "uav-propulsion",
    accent: "orange",
    images: [
      "/local-images/f2a5a2ce9ac2.jpg",
      "/local-images/b3ab7066a90f.jpg",
    ],
    zh: {
      title: "XH-EPS40 电动动力总成",
      subtitle: "多旋翼无人机高效电动推进系统",
      tags: ["电动推进", "多旋翼", "高效率", "轻量化"],
      specs: [
        { label: "额定功率", value: "40 kW" },
        { label: "系统效率", value: "≥93%" },
        { label: "总成重量", value: "4.2 kg" },
        { label: "最大转速", value: "6,800 RPM" },
        { label: "冷却方式", value: "液冷" },
        { label: "防护等级", value: "IP55" },
      ],
      desc: "XH-EPS40 是专为大型多旋翼无人机设计的高效电动动力总成，包含高功率密度无刷电机、高性能电子调速器（ESC）及专用螺旋桨。系统通过一体化热管理设计，确保在高温高载条件下长时间稳定工作。",
      features: [
        "高功率密度无刷电机，功率重量比 9.5 kW/kg",
        "液冷一体化散热，持续高载飞行稳定",
        "高频 FOC 矢量控制 ESC，响应时间 <2ms",
        "智能 PMU 实时功率监控与分配",
        "CAN/PWM 双模式控制接口",
      ],
    },
    en: {
      title: "XH-EPS40 Electric Propulsion System",
      subtitle: "High-Efficiency Electric Drive for Multi-Rotor UAVs",
      tags: ["Electric Drive", "Multi-Rotor", "High Efficiency", "Lightweight"],
      specs: [
        { label: "Rated Power", value: "40 kW" },
        { label: "System Efficiency", value: "≥93%" },
        { label: "System Weight", value: "4.2 kg" },
        { label: "Max RPM", value: "6,800 RPM" },
        { label: "Cooling", value: "Liquid Cooled" },
        { label: "Protection", value: "IP55" },
      ],
      desc: "The XH-EPS40 is a high-efficiency electric propulsion system for large multi-rotor UAVs, comprising a high power density brushless motor, high-performance ESC, and dedicated propellers.",
      features: [
        "High power density brushless motor, power-to-weight ratio 9.5 kW/kg",
        "Integrated liquid cooling for sustained high-load flight stability",
        "High-frequency FOC vector control ESC, response time <2ms",
        "Intelligent PMU for real-time power monitoring and distribution",
        "CAN/PWM dual-mode control interface",
      ],
    },
  },
  {
    id: "uav-002",
    category: "uav-propulsion",
    accent: "orange",
    images: [
      "/local-images/f03a220c06ce.jpg",
      "/local-images/07c6a622f66f.jpg",
    ],
    zh: {
      title: "XH-HPS15 混合动力总成",
      subtitle: "长航时固定翼无人机混动推进系统",
      tags: ["混合动力", "长航时", "固定翼", "增程系统"],
      specs: [
        { label: "发电功率", value: "15 kW" },
        { label: "续航时间", value: "8~12 小时" },
        { label: "总成重量", value: "18 kg" },
        { label: "燃油类型", value: "92#汽油/重油" },
        { label: "发动机排量", value: "800 cm³" },
        { label: "噪声水平", value: "≤75 dB@1m" },
      ],
      desc: "XH-HPS15 混合动力系统将小型航空活塞发动机与高效发电机及锂电池相结合，大幅延长无人机航时至8~12小时，是传统纯电方案的3~4倍。系统内置智能能量管理控制器，可根据飞行状态动态调配发动机与电池的输出比例。",
      features: [
        "串联混动架构，发动机始终工作在最优效率区间",
        "智能能量管理控制器，动态调配功率来源",
        "轻量化一体化集成设计，快速换装 <15 分钟",
        "支持多种燃油类型，野外任务适应性强",
        "实时状态监控与故障自诊断",
      ],
    },
    en: {
      title: "XH-HPS15 Hybrid Propulsion System",
      subtitle: "Long-Endurance Hybrid Drive for Fixed-Wing UAVs",
      tags: ["Hybrid Power", "Long Endurance", "Fixed-Wing", "Range Extender"],
      specs: [
        { label: "Generator Power", value: "15 kW" },
        { label: "Endurance", value: "8~12 hours" },
        { label: "System Weight", value: "18 kg" },
        { label: "Fuel Type", value: "Gasoline / Heavy Fuel" },
        { label: "Engine Displacement", value: "800 cm³" },
        { label: "Noise Level", value: "≤75 dB@1m" },
      ],
      desc: "The XH-HPS15 hybrid system combines a small aviation piston engine with a high-efficiency generator and lithium battery pack, extending UAV endurance to 8-12 hours — 3-4x that of pure-electric solutions.",
      features: [
        "Series hybrid architecture keeps engine at optimal efficiency",
        "Intelligent energy management controller for dynamic power allocation",
        "Lightweight integrated design, quick swap in <15 minutes",
        "Multi-fuel compatibility for field mission adaptability",
        "Real-time status monitoring and fault self-diagnosis",
      ],
    },
  },
  {
    id: "pm-001",
    category: "precision-mfg",
    accent: "emerald",
    images: [
      "/local-images/9aea0e61fa40.jpg",
      "/local-images/d034b5d88082.jpg",
      "/local-images/7945debf1b0d.jpg",
    ],
    zh: {
      title: "涡轮叶片精密加工",
      subtitle: "航空级涡轮/压气机叶片定制制造",
      tags: ["涡轮叶片", "单晶合金", "精密加工", "热障涂层"],
      specs: [
        { label: "材料体系", value: "单晶/定向凝固高温合金" },
        { label: "尺寸精度", value: "±0.01 mm" },
        { label: "表面粗糙度", value: "Ra ≤0.4 μm" },
        { label: "最高使用温度", value: "1,150°C" },
        { label: "涂层工艺", value: "EB-PVD 热障涂层" },
        { label: "检测标准", value: "HB/GJB 航空标准" },
      ],
      desc: "依托五轴联动数控加工中心与电火花加工设备，提供高温合金、钛合金涡轮及压气机叶片的精密制造服务。具备完整的叶片气膜孔加工能力（激光/EDM），并配套 EB-PVD 热障涂层及渗铝处理工艺，满足高温发动机严苛的服役要求。",
      features: [
        "五轴联动加工，复杂曲面一次成形",
        "激光及 EDM 气膜孔加工，孔径精度 ±0.02mm",
        "EB-PVD 热障涂层，隔热效果显著",
        "X 射线/荧光渗透全检，零缺陷交付",
        "支持单晶、定向凝固、粉末冶金等多种材料",
      ],
    },
    en: {
      title: "Turbine Blade Precision Machining",
      subtitle: "Aerospace-Grade Turbine / Compressor Blade Manufacturing",
      tags: ["Turbine Blade", "Single Crystal", "Precision Machining", "TBC"],
      specs: [
        { label: "Materials", value: "Single Crystal / DS Superalloy" },
        { label: "Dimensional Accuracy", value: "±0.01 mm" },
        { label: "Surface Roughness", value: "Ra ≤0.4 μm" },
        { label: "Max Service Temp", value: "1,150°C" },
        { label: "Coating Process", value: "EB-PVD Thermal Barrier" },
        { label: "Inspection Standard", value: "HB/GJB Aviation Standards" },
      ],
      desc: "Leveraging 5-axis CNC machining centers and EDM equipment to provide precision manufacturing of high-temperature alloy and titanium alloy turbine and compressor blades with complete film cooling hole capabilities.",
      features: [
        "5-axis simultaneous machining for complex surfaces in one setup",
        "Laser and EDM film cooling holes with ±0.02mm precision",
        "EB-PVD thermal barrier coatings for superior thermal insulation",
        "X-ray and FPI 100% inspection, zero-defect delivery",
        "Supports single crystal, DS, and powder metallurgy materials",
      ],
    },
  },
  {
    id: "pm-002",
    category: "precision-mfg",
    accent: "emerald",
    images: [
      "/local-images/fb245e55ecd7.jpg",
      "/local-images/f7f88997364d.jpg",
    ],
    zh: {
      title: "发动机机匣制造",
      subtitle: "航空发动机机匣及壳体结构件加工",
      tags: ["机匣制造", "薄壁结构", "钛合金", "大型结构件"],
      specs: [
        { label: "最大外径", value: "Φ1,200 mm" },
        { label: "壁厚范围", value: "1.5 ~ 30 mm" },
        { label: "材料", value: "钛合金 / 铝合金 / 不锈钢" },
        { label: "形位公差", value: "≤0.03 mm" },
        { label: "检测方式", value: "三坐标 / CT 扫描" },
        { label: "年产能", value: "1,200 件/年" },
      ],
      desc: "专注于航空发动机压气机机匣、燃烧室机匣及涡轮机匣等核心结构件的精密加工制造。具备大直径薄壁件车铣复合加工能力，配合高精度三坐标测量与 CT 检测，确保每件产品符合严格的航空质量标准。",
      features: [
        "车铣复合加工，薄壁大件变形控制工艺",
        "Φ1,200mm 以内大型回转体加工能力",
        "钛合金专项切削工艺，刀具寿命优化",
        "三坐标+CT 扫描双重检测，100% 合格率",
        "焊接、热处理、表面处理一站式配套",
      ],
    },
    en: {
      title: "Engine Casing Manufacturing",
      subtitle: "Precision Machining of Engine Casings & Structural Components",
      tags: ["Casing Mfg", "Thin-Wall Structures", "Titanium", "Large Components"],
      specs: [
        { label: "Max OD", value: "Φ1,200 mm" },
        { label: "Wall Thickness", value: "1.5 ~ 30 mm" },
        { label: "Materials", value: "Titanium / Aluminum / Stainless Steel" },
        { label: "GD&T Tolerance", value: "≤0.03 mm" },
        { label: "Inspection", value: "CMM / CT Scanning" },
        { label: "Annual Capacity", value: "1,200 pcs/year" },
      ],
      desc: "Specialized in precision machining of compressor casings, combustion chamber casings, and turbine casings — core structural components of aero engines.",
      features: [
        "Turn-mill compound machining with thin-wall deformation control",
        "Capability for large rotational bodies up to Φ1,200mm",
        "Titanium-specific cutting processes with optimized tool life",
        "CMM + CT scanning dual inspection for 100% pass rate",
        "One-stop service: welding, heat treatment, and surface finishing",
      ],
    },
  },
  {
    id: "ts-001",
    category: "tech-services",
    accent: "violet",
    images: [
      "/local-images/28f8eb8e3860.jpg",
      "/local-images/7c63e47a4fd3.jpg",
    ],
    zh: {
      title: "发动机性能评估服务",
      subtitle: "航空发动机全系统性能测试与评估",
      tags: ["性能评估", "台架试验", "数据分析", "改进咨询"],
      specs: [
        { label: "测试推力范围", value: "0 ~ 500 kN" },
        { label: "测试转速范围", value: "0 ~ 30,000 RPM" },
        { label: "数据采集频率", value: "100 kHz" },
        { label: "测量参数", value: "500+ 通道" },
        { label: "试验台认证", value: "CAAC 认可试验机构" },
        { label: "报告周期", value: "5 个工作日" },
      ],
      desc: "提供涵盖地面台架试验、高空模拟试验及整机振动噪声测试的全套发动机性能评估服务。依托自主研发的高精度多通道数据采集系统，可实现500+参数同步采集与实时分析，为客户提供详尽的性能评估报告及优化改进建议。",
      features: [
        "500+ 通道同步数据采集，全参数覆盖",
        "高空模拟舱，模拟最高12km高度试验",
        "整机振动/噪声/红外特征综合测试",
        "CFD 仿真与实测数据融合分析",
        "CAAC 认可试验机构，报告具有法律效力",
      ],
    },
    en: {
      title: "Engine Performance Assessment",
      subtitle: "Full-System Aero Engine Performance Testing & Evaluation",
      tags: ["Performance Test", "Test Cell", "Data Analysis", "Optimization Advisory"],
      specs: [
        { label: "Thrust Range", value: "0 ~ 500 kN" },
        { label: "Speed Range", value: "0 ~ 30,000 RPM" },
        { label: "Data Sampling Rate", value: "100 kHz" },
        { label: "Measured Parameters", value: "500+ Channels" },
        { label: "Lab Certification", value: "CAAC Accredited" },
        { label: "Report Turnaround", value: "5 Business Days" },
      ],
      desc: "Comprehensive engine performance evaluation covering ground test cells, altitude simulation tests, and full-engine vibration/noise testing.",
      features: [
        "500+ channel synchronous data acquisition with full parameter coverage",
        "Altitude simulation chamber replicating conditions up to 12km",
        "Integrated vibration/noise/infrared signature testing",
        "CFD simulation and test data fusion analysis",
        "CAAC accredited laboratory with legally recognized test reports",
      ],
    },
  },
  {
    id: "ts-002",
    category: "tech-services",
    accent: "violet",
    images: [
      "/local-images/5fb93bb9005d.jpg",
      "/local-images/d04f1c48c454.jpg",
    ],
    zh: {
      title: "适航与技术咨询",
      subtitle: "航空动力适航认证全程技术支持",
      tags: ["适航认证", "CCAR-33", "技术咨询", "体系建设"],
      specs: [
        { label: "适用法规", value: "CCAR-33 / FAR-33 / CS-E" },
        { label: "服务范围", value: "型号合格审定全阶段" },
        { label: "专家团队", value: "20+ 适航审定专家" },
        { label: "成功案例", value: "15+ 型号完成取证" },
        { label: "培训课时", value: "80课时/套餐" },
        { label: "交付物", value: "完整符合性文件包" },
      ],
      desc: "提供覆盖 CCAR-33、FAR-33、CS-E 的航空发动机适航认证全程技术支持，包括适航基础计划制定、符合性方法确定、试验验证方案策划及完整符合性文件编制。配套提供适航体系建设咨询与人才培训。",
      features: [
        "CCAR-33/FAR-33/CS-E 多标准适航支持",
        "型号合格审定全阶段覆盖",
        "符合性文件包编制与审查辅导",
        "适航体系建设与持续适航管理",
        "定制化适航工程师培训课程",
      ],
    },
    en: {
      title: "Airworthiness & Technical Advisory",
      subtitle: "Full-Process Technical Support for Engine Certification",
      tags: ["Airworthiness", "CCAR-33", "Technical Advisory", "System Building"],
      specs: [
        { label: "Applicable Regs", value: "CCAR-33 / FAR-33 / CS-E" },
        { label: "Service Scope", value: "Full Type Certification Phases" },
        { label: "Expert Team", value: "20+ Certification Specialists" },
        { label: "Success Cases", value: "15+ Types Certified" },
        { label: "Training Hours", value: "80 hrs/package" },
        { label: "Deliverables", value: "Complete Compliance Document Package" },
      ],
      desc: "Full-process technical support for aero engine airworthiness certification covering CCAR-33, FAR-33, and CS-E, from certification basis planning, means of compliance, test plan development, to complete compliance document preparation.",
      features: [
        "Multi-standard support: CCAR-33 / FAR-33 / CS-E",
        "Full coverage of type certification phases",
        "Compliance documentation preparation and review guidance",
        "Airworthiness system construction and continuing airworthiness management",
        "Customized airworthiness engineer training programs",
      ],
    },
  },
];

// ─── Default content ─────────────────────────────────────────────────────────

export const defaultSocialLinks: SocialLink[] = [
  { id: "wechat", icon: "ri-wechat-line", label: "微信", href: "weixin://dl/chat?ch98701", visible: true },
  { id: "weibo", icon: "ri-weibo-line", label: "微博", href: "#", visible: true },
  { id: "linkedin", icon: "ri-linkedin-line", label: "LinkedIn", href: "#", visible: true },
  { id: "email", icon: "ri-mail-line", label: "邮件", href: "mailto:175153762@qq.com", visible: true },
];

export const defaultFooterLegal: FooterLegal = {
  copyright: "© 2024 Aero Propulsion Technology Co., Ltd.",
  privacyZh: "隐私政策",
  privacyEn: "Privacy Policy",
  privacyHref: "#",
  termsZh: "服务条款",
  termsEn: "Terms of Service",
  termsHref: "#",
  icpZh: "京ICP备XXXXXXXX号",
  icpEn: "ICP License",
  icpHref: "#",
};

export const defaultBgMusic: BgMusic = {
  name: "暖场音乐",
  enabled: true,
  url: "/3%E6%9A%96%E5%9C%BA.mp3",
  volume: 0.4,
  strategy: "audible-first",
};

export const defaultAIChat: AIChatConfig = {
  enabled: true,
  endpoint: "",
};

export const defaultTechServiceDocs: TechServiceDoc[] = [
  {
    id: "ts-doc-001",
    order: 1,
    zhTitle: "技术服务总纲",
    enTitle: "Technical Service Overview",
    zhSummary: "介绍技术服务能力边界、交付方式与协同机制。",
    enSummary: "Defines service scope, delivery mode, and collaboration workflow.",
    zhContentHtml: "<h2>技术服务总纲</h2><p>本页可在后台自由排版，支持插入图片和视频，建议先写服务背景、目标客户与交付范围。</p>",
    enContentHtml: "<h2>Technical Service Overview</h2><p>This page supports free layout editing in admin, including image and video insertion.</p>",
  },
  {
    id: "ts-doc-002",
    order: 2,
    zhTitle: "适航咨询与认证辅导",
    enTitle: "Airworthiness Advisory",
    zhSummary: "面向型号审定的技术支持、文档编制与审查陪同。",
    enSummary: "Certification support, compliance documentation, and review assistance.",
    zhContentHtml: "<h2>适航咨询与认证辅导</h2><p>可按项目阶段拆分：需求澄清、符合性路径、试验验证、审查答辩。</p>",
    enContentHtml: "<h2>Airworthiness Advisory</h2><p>Organize by project phase: requirement clarification, compliance path, validation, and authority review.</p>",
  },
  {
    id: "ts-doc-003",
    order: 3,
    zhTitle: "系统优化与培训服务",
    enTitle: "System Optimization & Training",
    zhSummary: "覆盖性能优化、故障定位、工程师培训与能力建设。",
    enSummary: "Covers optimization, troubleshooting, engineer training and capability building.",
    zhContentHtml: "<h2>系统优化与培训服务</h2><p>建议放置案例、指标提升前后对比，以及培训课程结构。</p>",
    enContentHtml: "<h2>System Optimization & Training</h2><p>Recommended content: case studies, KPI improvements, and training curriculum.</p>",
  },
];

export const defaultNewsArticles: NewsArticle[] = [
  {
    id: "news-001",
    order: 1,
    publishDate: "2026-05-05",
    zhTitle: "新闻发布栏目上线",
    enTitle: "News Center Launched",
    zhSummary: "支持以高级文档方式发布图文视频新闻事件。",
    enSummary: "Publish rich news events with advanced document editing.",
    zhContentHtml: "<h2>新闻发布栏目上线</h2><p>现在可以在后台以高级文档形式创建新闻，支持插入图片、视频预览、自由排版和内容发布。</p>",
    enContentHtml: "<h2>News Center Launched</h2><p>You can now create news posts with rich document editing, including images, video previews, and free layout.</p>",
  },
];

export const defaultFriendLinks: FriendLink[] = [
  { label: "中国航空工业集团", href: "https://www.avic.com" },
  { label: "中国民用航空局", href: "https://www.caac.gov.cn" },
  { label: "无人系统产业联盟", href: "#" },
  { label: "航空发动机重大专项", href: "#" },
];

export const defaultContent: SiteContent = {
  nav: {
    logoImage:
      "/local-images/c1919b7175e0.png",
    logoAlt: "Company Logo",
    ogTitle: "航空发动机 | 内燃发动机·无人机动力·涡喷发动机·精密制造·",
    items: [
      { zh: "首页", en: "Home", href: "#banner", scroll: true },
      { zh: "产品展示", en: "Products", href: "/products", scroll: false },
      { zh: "关于我们", en: "About Us", href: "#about", scroll: true },
      { zh: "联系我们", en: "Contact", href: "#contact", scroll: true },
    ],
  },
  bannerSlides: [
    {
      zhTitle: "驱动未来飞行",
      zhSub: "专注航空发动机与无人机动力总成研发制造",
      enTitle: "Powering Future Flight",
      enSub: "Specialized in Aero Engine & UAV Propulsion R&D",
      image:
        "/local-images/879c987cc945.jpg",
    },
    {
      zhTitle: "精工动力，极致性能",
      zhSub: "无人机动力系统全套解决方案供应商",
      enTitle: "Precision Power, Peak Performance",
      enSub: "Full-Stack UAV Propulsion Solution Provider",
      image:
        "/local-images/0cdd96fbf5ad.jpg",
    },
    {
      zhTitle: "创新驱动，引领行业",
      zhSub: "以技术创新推动航空动力产业升级",
      enTitle: "Innovation Driven, Industry Leading",
      enSub: "Advancing Aerospace Propulsion Through Innovation",
      image:
        "/local-images/1c3554cb8a38.jpg",
    },
    {
      zhTitle: "精工机械",
      zhSub: "副标题说明文字",
      enTitle: "New Slide Title",
      enSub: "Subtitle description",
      image: "/local-images/2856d5b1a566.jpg",
    },
  ],
  aboutStats: [
    { num: "5+", zhLabel: "年研发经验", enLabel: "Years R&D Experience" },
    { num: "80+", zhLabel: "核心专利", enLabel: "Core Patents" },
    { num: "200+", zhLabel: "专业技术人员", enLabel: "Technical Experts" },
    { num: "30+", zhLabel: "服务客户", enLabel: "Clients Served" },
  ],
  aboutZhDesc1:
    "公司成立于2020年，是一家专注于航空发动机与无人机动力总成研发、制造及技术服务的高新技术企业。公司深耕航空动力领域逾15年，汇聚了一批来自国内顶尖高校及航空科研院所的专业技术人才，拥有完整的研发、试验、制造与认证体系。",
  aboutZhDesc2:
    "我们以创新驱动、质量为本、客户至上为核心理念，始终致力于为国防、民用航空及商业无人机领域提供高性能、高可靠性的动力解决方案，助力中国航空产业走向世界前列。",
  aboutEnDesc1:
    "Founded in 2020, we are a high-tech enterprise specializing in the R&D, manufacturing and technical services of aero engines and UAV propulsion systems. With over 15 years of deep expertise, we have assembled top talent from leading universities and aerospace research institutes.",
  aboutEnDesc2:
    "Our core philosophy of \"Innovation-Driven, Quality-First, Customer-Centric\" drives us to deliver high-performance, reliable propulsion solutions for defense, civil aviation and commercial UAV industries.",
  aboutImage:
    "/local-images/c23184d818b8.jpg",
  products: [
    {
      zhTitle: "航空发动机研发",
      zhDesc:
        "专注于涡扇、涡轴及活塞发动机的研发与制造，拥有完整的设计、仿真、试验与认证能力，为多型飞行器提供可靠动力。",
      enTitle: "Aero Engine R&D",
      enDesc:
        "Focused on turbofan, turboshaft, and piston engine development with full design, simulation, testing and certification capabilities.",
      image:
        "/local-images/04329afed501.jpg",
    },
    {
      zhTitle: "无人机动力总成",
      zhDesc:
        "提供无人机电动与混动动力总成系统的一站式研制，涵盖电机、电调、螺旋桨及集成管理系统，支持定制化开发。",
      enTitle: "UAV Propulsion System",
      enDesc:
        "One-stop electric and hybrid UAV propulsion development, covering motors, ESCs, propellers and integrated management systems.",
      image:
        "/local-images/b9da51236a8d.jpg",
    },
    {
      zhTitle: "零部件精密制造",
      zhDesc:
        "依托先进的数控加工与增材制造工艺，生产各类航空级精密零部件，满足高强度、轻量化、严公差的行业标准。",
      enTitle: "Precision Parts Manufacturing",
      enDesc:
        "Advanced CNC machining and additive manufacturing to produce aerospace-grade precision parts meeting high-strength and tight-tolerance standards.",
      image:
        "/local-images/d5a88cc89ff1.jpg",
    },
    {
      zhTitle: "技术服务与咨询",
      zhDesc:
        "提供航空动力系统性能评估、改进优化、适航咨询及技术培训服务，助力客户提升自主研制能力与产品竞争力。",
      enTitle: "Technical Services & Consulting",
      enDesc:
        "Performance evaluation, optimization, airworthiness consulting and technical training to boost clients' independent R&D capabilities.",
      image:
        "/local-images/dc7fe072673c.jpg",
    },
  ],
  contact: {
    zhTitle: "期待与您合作",
    enTitle: "Ready to Work Together",
    zhSubtitle: "无论您有任何技术问题或合作意向，欢迎随时联系我们的专业团队",
    enSubtitle:
      "Whether you have technical questions or collaboration intentions, our professional team is always ready",
    zhFormTitle: "发送消息",
    enFormTitle: "Send a Message",
    mapEmbedUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3057!2d116.3!3d40.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDAw!5e0!3m2!1sen!2scn!4v1699999999999",
    infoItems: [
      {
        icon: "ri-map-pin-2-line",
        zhLabel: "公司地址",
        enLabel: "Address",
        zhValue: "北京市海淀区航天城科技园 A栋 8层",
        enValue: "Floor 8, Bldg A, Aerospace Tech Park, Haidian, Beijing",
      },
      {
        icon: "ri-phone-line",
        zhLabel: "联系电话",
        enLabel: "Phone",
        zhValue: "+86 18176708838  微信同号",
        enValue: "+86 18176708838  WeChat same number",
      },
      {
        icon: "ri-mail-line",
        zhLabel: "电子邮箱",
        enLabel: "Email",
        zhValue: "175153762@qq.com",
        enValue: "175153762@qq.com",
      },
      {
        icon: "ri-time-line",
        zhLabel: "工作时间",
        enLabel: "Business Hours",
        zhValue: "周一至周五 9:00 - 18:00",
        enValue: "Mon - Fri  9:00 - 18:00",
      },
    ],
  },
  productsDetail: defaultProductsDetail,
  productCategories: defaultProductCategories,
  friendLinks: defaultFriendLinks,
  socialLinks: defaultSocialLinks,
  footerLegal: defaultFooterLegal,
  bgMusic: defaultBgMusic,
  aiChat: defaultAIChat,
  techServiceDocs: defaultTechServiceDocs,
  newsArticles: defaultNewsArticles,
};

// ─── Storage helpers ──────────────────────────────────────────────────────────

function loadContentFromCache(): SiteContent {
  return getCurrentContent();
}

export function getContent(): SiteContent {
  return getCurrentContent();
}

export function forceBroadcastUpdate(): void {
  window.dispatchEvent(new CustomEvent("site-content-updated"));
  broadcastContentUpdate();
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useContentStore() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    // 订阅全局 store 变化
    const listener = () => forceUpdate(n => n + 1);
    _listeners.add(listener);
    // 启动全局初始化（只执行一次）
    initGlobalStore();
    return () => { _listeners.delete(listener); };
  }, []);

  const content = getCurrentContent();
  const loading = _loading;

  const update = useCallback((updater: (prev: SiteContent) => SiteContent) => {
    const next = updater(getCurrentContent());
    setGlobalContent(next);
    window.dispatchEvent(new CustomEvent("site-content-updated"));
    broadcastContentUpdate();
  }, []);

  /** 保存并同步到部署数据库（由「保存并同步」按钮触发） */
  const saveToCloud = useCallback(async (currentContent: SiteContent): Promise<SaveResult> => {
    try {
      await saveToServer(currentContent);
      console.log("[saveToCloud] SUCCESS - saved to server");
      return { ok: true };
    } catch (err) {
      console.error("[saveToCloud] FAILED:", err);
      return { ok: false, error: err instanceof Error ? err.message : "保存失败" };
    }
  }, []);

  const reset = useCallback(async () => {
    setGlobalContent({ ...defaultContent });
    try {
      await saveToServer(defaultContent);
    } catch (err) {
      console.warn("[reset] Server save skipped:", err);
    }
    window.dispatchEvent(new CustomEvent("site-content-updated"));
    broadcastContentUpdate();
  }, []);

  // ---- Nav ----
  const updateNavField = useCallback(
    (key: keyof Omit<NavContent, "items">, val: string) => {
      update((prev) => ({ ...prev, nav: { ...prev.nav, [key]: val } }));
    },
    [update]
  );
  const updateNavItem = useCallback(
    (idx: number, key: keyof NavItem, val: string) => {
      update((prev) => {
        const items = [...prev.nav.items];
        items[idx] = { ...items[idx], [key]: val };
        return { ...prev, nav: { ...prev.nav, items } };
      });
    },
    [update]
  );
  const addNavItem = useCallback(() => {
    update((prev) => ({
      ...prev,
      nav: { ...prev.nav, items: [...prev.nav.items, { zh: "新菜单项", en: "New Item", href: "#", scroll: true }] },
    }));
  }, [update]);
  const removeNavItem = useCallback(
    (idx: number) => {
      update((prev) => {
        if (prev.nav.items.length <= 1) return prev;
        return { ...prev, nav: { ...prev.nav, items: prev.nav.items.filter((_, i) => i !== idx) } };
      });
    },
    [update]
  );

  // ---- Banner ----
  const addBannerSlide = useCallback(() => {
    update((prev) => ({
      ...prev,
      bannerSlides: [
        ...prev.bannerSlides,
        {
          zhTitle: "新幻灯片标题",
          zhSub: "副标题说明文字",
          enTitle: "New Slide Title",
          enSub: "Subtitle description",
          image:
            "/local-images/558ad09ae08c.jpg" +
            Date.now() +
            "&orientation=landscape",
        },
      ],
    }));
  }, [update]);
  const removeBannerSlide = useCallback(
    (idx: number) => {
      update((prev) => {
        if (prev.bannerSlides.length <= 1) return prev;
        return { ...prev, bannerSlides: prev.bannerSlides.filter((_, i) => i !== idx) };
      });
    },
    [update]
  );

  // ---- Products (homepage cards) ----
  const addProduct = useCallback(() => {
    update((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          zhTitle: "新产品/服务",
          zhDesc: "在这里填写产品或服务的中文描述，介绍核心功能与优势。",
          enTitle: "New Product / Service",
          enDesc: "Enter the product or service description here, highlighting core features and advantages.",
          image:
            "/local-images/607194abbfeb.jpg" +
            Date.now() +
            "&orientation=landscape",
        },
      ],
    }));
  }, [update]);
  const removeProduct = useCallback(
    (idx: number) => {
      update((prev) => {
        if (prev.products.length <= 1) return prev;
        return { ...prev, products: prev.products.filter((_, i) => i !== idx) };
      });
    },
    [update]
  );

  // ---- About Stats ----
  const addStat = useCallback(() => {
    update((prev) => ({
      ...prev,
      aboutStats: [...prev.aboutStats, { num: "0+", zhLabel: "新指标", enLabel: "New Metric" }],
    }));
  }, [update]);
  const removeStat = useCallback(
    (idx: number) => {
      update((prev) => {
        if (prev.aboutStats.length <= 1) return prev;
        return { ...prev, aboutStats: prev.aboutStats.filter((_, i) => i !== idx) };
      });
    },
    [update]
  );

  // ---- Contact ----
  const addContactInfo = useCallback(() => {
    update((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        infoItems: [
          ...prev.contact.infoItems,
          { icon: "ri-information-line", zhLabel: "新联系方式", enLabel: "New Contact", zhValue: "请填写内容", enValue: "Please fill in" },
        ],
      },
    }));
  }, [update]);
  const removeContactInfo = useCallback(
    (idx: number) => {
      update((prev) => {
        if (prev.contact.infoItems.length <= 1) return prev;
        return { ...prev, contact: { ...prev.contact, infoItems: prev.contact.infoItems.filter((_, i) => i !== idx) } };
      });
    },
    [update]
  );
  const updateContactInfo = useCallback(
    (idx: number, key: keyof ContactInfoItem, val: string) => {
      update((prev) => {
        const items = [...prev.contact.infoItems];
        items[idx] = { ...items[idx], [key]: val };
        return { ...prev, contact: { ...prev.contact, infoItems: items } };
      });
    },
    [update]
  );
  const updateContactField = useCallback(
    (key: keyof Omit<ContactContent, "infoItems">, val: string) => {
      update((prev) => ({ ...prev, contact: { ...prev.contact, [key]: val } }));
    },
    [update]
  );

  // ---- Products Detail CRUD ----
  const addProductDetail = useCallback(() => {
    const newId = "pd-" + Date.now();
    update((prev) => ({
      ...prev,
      productsDetail: [
        ...prev.productsDetail,
        {
          id: newId,
          category: prev.productCategories[0]?.id ?? "aero-engine",
          accent: "cyan",
          images: [
            "/local-images/fc2117959e26.jpg" + Date.now() + "&orientation=landscape",
          ],
          zh: {
            title: "新产品名称",
            subtitle: "产品副标题",
            tags: ["标签1", "标签2"],
            specs: [
              { label: "参数名称", value: "参数值" },
            ],
            desc: "请在这里填写产品的中文详细描述，介绍产品的核心功能、技术特点与应用场景。",
            features: ["核心优势1", "核心优势2"],
          },
          en: {
            title: "New Product Name",
            subtitle: "Product Subtitle",
            tags: ["Tag1", "Tag2"],
            specs: [
              { label: "Spec Name", value: "Spec Value" },
            ],
            desc: "Please fill in the English product description here, covering core functions, technical highlights, and application scenarios.",
            features: ["Key Feature 1", "Key Feature 2"],
          },
        },
      ],
    }));
  }, [update]);

  const removeProductDetail = useCallback(
    (idx: number) => {
      update((prev) => {
        if (prev.productsDetail.length <= 1) return prev;
        return { ...prev, productsDetail: prev.productsDetail.filter((_, i) => i !== idx) };
      });
    },
    [update]
  );

  const updateProductDetail = useCallback(
    (idx: number, updater: (p: ProductDetail) => ProductDetail) => {
      update((prev) => {
        const list = [...prev.productsDetail];
        list[idx] = updater(list[idx]);
        return { ...prev, productsDetail: list };
      });
    },
    [update]
  );

  // ---- Product Categories CRUD ----
  const updateProductCategory = useCallback(
    (idx: number, key: keyof ProductCategory, val: string) => {
      update((prev) => {
        const cats = [...prev.productCategories];
        cats[idx] = { ...cats[idx], [key]: val };
        return { ...prev, productCategories: cats };
      });
    },
    [update]
  );
  const addProductCategory = useCallback(() => {
    const newId = "cat-" + Date.now();
    update((prev) => ({
      ...prev,
      productCategories: [
        ...prev.productCategories,
        { id: newId, zh: "新分类", en: "New Category" },
      ],
    }));
  }, [update]);
  const removeProductCategory = useCallback(
    (idx: number) => {
      update((prev) => {
        if (prev.productCategories.length <= 1) return prev;
        const removedId = prev.productCategories[idx].id;
        const cats = prev.productCategories.filter((_, i) => i !== idx);
        // reassign products in removed category to the first remaining category
        const fallbackId = cats[0].id;
        const products = prev.productsDetail.map((p) =>
          p.category === removedId ? { ...p, category: fallbackId } : p
        );
        return { ...prev, productCategories: cats, productsDetail: products };
      });
    },
    [update]
  );

  // ---- Friend Links ----
  const addFriendLink = useCallback(() => {
    update((prev) => ({
      ...prev,
      friendLinks: [...prev.friendLinks, { label: "新友情链接", href: "#" }],
    }));
  }, [update]);
  const removeFriendLink = useCallback(
    (idx: number) => {
      update((prev) => {
        if (prev.friendLinks.length <= 1) return prev;
        return { ...prev, friendLinks: prev.friendLinks.filter((_, i) => i !== idx) };
      });
    },
    [update]
  );
  const updateFriendLink = useCallback(
    (idx: number, key: keyof FriendLink, val: string) => {
      update((prev) => {
        const links = [...prev.friendLinks];
        links[idx] = { ...links[idx], [key]: val };
        return { ...prev, friendLinks: links };
      });
    },
    [update]
  );

  // ---- Product ID update ----
  const updateProductId = useCallback(
    (idx: number, newId: string) => {
      update((prev) => {
        const list = [...prev.productsDetail];
        list[idx] = { ...list[idx], id: newId };
        return { ...prev, productsDetail: list };
      });
    },
    [update]
  );

  const addTechServiceDoc = useCallback(() => {
    update((prev) => {
      const maxOrder = prev.techServiceDocs.reduce((max, item) => Math.max(max, item.order), 0);
      const next: TechServiceDoc = {
        id: `ts-doc-${Date.now()}`,
        order: maxOrder + 1,
        zhTitle: "新文档",
        enTitle: "New Document",
        zhSummary: "请填写中文简介",
        enSummary: "Please fill summary",
        zhContentHtml: "<h2>新文档</h2><p>请输入正文内容。</p>",
        enContentHtml: "<h2>New Document</h2><p>Please edit content.</p>",
      };
      return { ...prev, techServiceDocs: [...prev.techServiceDocs, next] };
    });
  }, [update]);

  const removeTechServiceDoc = useCallback((idx: number) => {
    update((prev) => ({
      ...prev,
      techServiceDocs: prev.techServiceDocs.filter((_, i) => i !== idx),
    }));
  }, [update]);

  const updateTechServiceDoc = useCallback((idx: number, key: keyof TechServiceDoc, val: string | number) => {
    update((prev) => {
      const list = [...prev.techServiceDocs];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, techServiceDocs: list };
    });
  }, [update]);

  const addNewsArticle = useCallback(() => {
    update((prev) => {
      const maxOrder = prev.newsArticles.reduce((max, item) => Math.max(max, item.order), 0);
      const next: NewsArticle = {
        id: `news-${Date.now()}`,
        order: maxOrder + 1,
        publishDate: new Date().toISOString().slice(0, 10),
        zhTitle: "新新闻事件",
        enTitle: "New News Event",
        zhSummary: "请填写新闻摘要",
        enSummary: "Please fill in the summary",
        zhContentHtml: "<h2>新新闻事件</h2><p>请输入新闻正文内容。</p>",
        enContentHtml: "<h2>New News Event</h2><p>Please edit the news content.</p>",
      };
      return { ...prev, newsArticles: [...prev.newsArticles, next] };
    });
  }, [update]);

  const removeNewsArticle = useCallback((idx: number) => {
    update((prev) => ({
      ...prev,
      newsArticles: prev.newsArticles.filter((_, i) => i !== idx),
    }));
  }, [update]);

  const updateNewsArticle = useCallback((idx: number, key: keyof NewsArticle, val: string | number) => {
    update((prev) => {
      const list = [...prev.newsArticles];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, newsArticles: list };
    });
  }, [update]);

  return {
    content,
    loading,
    update,
    reset,
    saveToCloud,
    defaultContent,
    updateNavField,
    updateNavItem,
    addNavItem,
    removeNavItem,
    addBannerSlide,
    removeBannerSlide,
    addProduct,
    removeProduct,
    addStat,
    removeStat,
    addContactInfo,
    removeContactInfo,
    updateContactInfo,
    updateContactField,
    addProductDetail,
    removeProductDetail,
    updateProductDetail,
    updateProductCategory,
    addProductCategory,
    removeProductCategory,
    updateProductId,
    addTechServiceDoc,
    removeTechServiceDoc,
    updateTechServiceDoc,
    addNewsArticle,
    removeNewsArticle,
    updateNewsArticle,
    addFriendLink,
    removeFriendLink,
    updateFriendLink,
    updateSocialLink: useCallback((idx: number, key: keyof SocialLink, val: string | boolean) => {
      update((prev) => {
        const links = [...prev.socialLinks];
        links[idx] = { ...links[idx], [key]: val };
        return { ...prev, socialLinks: links };
      });
    }, [update]),
    updateFooterLegal: useCallback((key: keyof FooterLegal, val: string) => {
      update((prev) => ({ ...prev, footerLegal: { ...prev.footerLegal, [key]: val } }));
    }, [update]),
    updateAIChat: useCallback((key: keyof AIChatConfig, val: string | boolean) => {
      update((prev) => ({ ...prev, aiChat: { ...prev.aiChat, [key]: val } }));
    }, [update]),
    updateBgMusic: useCallback((key: keyof BgMusic, val: string | boolean | number) => {
      update((prev) => ({ ...prev, bgMusic: { ...prev.bgMusic, [key]: val } }));
    }, [update]),
  };
}

// useReadContent 已废弃，请直接使用 useRemoteContent from @/lib/ContentContext
// 保留此函数仅为向下兼容
export function useReadContent(): SiteContent {
  return loadContentFromCache();
}
